import { memo, useCallback, useRef, useState, type MouseEvent } from 'react'
import { MenuDescription } from './MenuDescription'
import { Link } from 'react-router-dom'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DropdownMenu, type MenuSection } from '../../shared/components/DropdownMenu'
import { POPOVER_MENU_ICON_SIZE_PX } from '@features/shared/components/PopoverMenu'
import { IconButton } from '@features/shared/components/IconButton'
import {
  DatabaseIcon,
  EyeOffIcon,
  GearIcon,
  HeartIcon,
  HomeIcon,
  KebabIcon,
} from '../../shared/icons/icons'
import { Path } from '../../../routes/paths'
import { useGlobalSidebarStore } from '../stores/useGlobalSidebarStore'
import { useTableListActions } from './tableListActions'
import type { TableSidebarItem } from '../api/contracts'
import {
  useFavoriteChanges,
  useIsFavorite,
  useIsHomePage,
  useSetHomePage,
} from '../hooks/useFavorites'
import {
  ADD_TO_FAVORITES_LABEL,
  FavoriteToggle,
  REMOVE_FROM_FAVORITES_LABEL,
  SET_AS_HOME_PAGE_LABEL,
} from './FavoriteToggle'
import { useHiddenChanges, useIsHidden } from '../hooks/useHiddenPages'
import { menuRowPathProps } from '../hooks/useMenuFilter'
import { HIDE_LABEL, HiddenToggle, UNHIDE_LABEL } from './HiddenToggle'
import { DRAG_SWAPPED_ICON_CLASS, LeadingDragHandle } from './LeadingDragHandle'
import {
  MENU_ICON_SLOT_CLASS,
  MENU_ICON_SLOTS_CLASS,
  MENU_ROW_END_PAD_CLASS,
  menuIconSlotClass,
  menuIconSlotStates,
  menuIconSlotsLeadClass,
} from './menuIconSlots'
import FadeTruncatedText from '@features/shared/components/FadeTruncatedText'

interface TableLinkProps {
  table: TableSidebarItem
  /** Indent in pixels (used inside folders). Defaults to 0. */
  indent?: number
  /** Absolute left edge for the table label within the sidebar list. */
  labelInset?: number
  /** Left edge for the hover/active background when it differs from content indent. */
  highlightInset?: number
  sortableId?: string
}

const DEFAULT_LABEL_PADDING_LEFT = 12
const TABLE_ICON_SIZE = 14
const TABLE_ICON_LABEL_GAP = 8

/**
 * A single table row in the sidebar. Visually identical to the original
 * TableList button — extracted so it can be reused inside folders and
 * outside (unorganized section). Memoized: a row re-renders only when its
 * own table, place, or active state changes, not on every navigation.
 */
function TableLink({ table, indent = 0, labelInset, highlightInset, sortableId }: TableLinkProps) {
  const { onSelect, openTableSettings } = useTableListActions()
  const [menuOpen, setMenuOpen] = useState(false)
  const rowRef = useRef<HTMLDivElement | null>(null)
  const isActive = useGlobalSidebarStore((s) => s.activeTableId === table.id)
  // The table's own address, which opens its default view.
  const tablePath = Path.tableRecords(table.id)
  const isFavorite = useIsFavorite(tablePath)
  const isHomePage = useIsHomePage(tablePath)
  const { add: addFavorite, remove: removeFavorite } = useFavoriteChanges()
  const isHidden = useIsHidden(tablePath)
  const { hide, unhide } = useHiddenChanges()
  const setHomePage = useSetHomePage()
  const nestedRowInset = highlightInset ?? (indent > 0 ? indent + 5 : 0)
  const labelLeft = labelInset ?? indent + DEFAULT_LABEL_PADDING_LEFT
  const contentLeft = Math.max(
    0,
    labelLeft - TABLE_ICON_SIZE - TABLE_ICON_LABEL_GAP - nestedRowInset
  )
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: sortableId ?? `table-${table.id}`,
    data: { type: 'table', tableId: table.id },
    disabled: !sortableId,
  })
  const setRowRef = useCallback(
    (node: HTMLDivElement | null) => {
      rowRef.current = node
      if (sortableId) setNodeRef(node)
    },
    [setNodeRef, sortableId]
  )

  const handleRouteClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation()
    if (!event.ctrlKey && !event.metaKey) onSelect?.()
  }

  // The same menu as Table options on the table's page.
  const [menuSlot, hiddenSlot, favoriteSlot] = menuIconSlotStates([
    menuOpen,
    isHidden || menuOpen,
    isFavorite || menuOpen,
  ])
  const tableOptionsSections: MenuSection[] = [
    {
      items: [
        {
          type: 'action',
          id: 'manage-table-settings',
          label: 'Manage table settings',
          icon: <GearIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () => {
            openTableSettings?.(table.id)
            onSelect?.()
          },
        },
      ],
    },
    {
      items: [
        {
          type: 'action',
          id: 'toggle-favorite',
          label: isFavorite ? REMOVE_FROM_FAVORITES_LABEL : ADD_TO_FAVORITES_LABEL,
          icon: <HeartIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () => (isFavorite ? removeFavorite(tablePath) : addFavorite(tablePath)),
        },
        {
          type: 'action',
          id: 'toggle-hidden',
          label: isHidden ? UNHIDE_LABEL : HIDE_LABEL,
          icon: <EyeOffIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          onClick: () =>
            isHidden ? unhide(tablePath) : hide(tablePath, table.display_name_plural),
        },
        {
          type: 'action',
          id: 'set-home-page',
          label: SET_AS_HOME_PAGE_LABEL,
          icon: <HomeIcon size={POPOVER_MENU_ICON_SIZE_PX} aria-hidden="true" />,
          omitted: isHomePage,
          onClick: () => setHomePage(tablePath, table.display_name_plural),
        },
      ],
    },
  ]

  return (
    <div
      ref={setRowRef}
      // Lets the list animate this row when a Recency re-sort moves it.
      data-sidebar-table-row={table.id}
      {...menuRowPathProps(tablePath)}
      style={{
        // While dragging, the in-list row is hidden; the DragOverlay renders the
        // moving copy and neighbors slide via the sort strategy.
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
        marginLeft: nestedRowInset,
        paddingLeft: contentLeft,
        width: nestedRowInset ? `calc(100% - ${nestedRowInset}px)` : '100%',
        minWidth: 0,
      }}
      className={`group relative flex h-[var(--sidebar-tile-size)] w-full min-w-0 items-center rounded-md ${MENU_ROW_END_PAD_CLASS} text-sm ${
        // Selected and hovered like the rail's items (the sidebar-item tokens);
        // a hidden table, shown, reads faint.
        isActive
          ? 'bg-[color:var(--sidebar-item-active-bg)] font-medium text-[color:var(--sidebar-item-active-text)] hover:bg-[color:var(--sidebar-item-active-hover-bg)]'
          : isHidden
            ? 'text-[color:var(--sidebar-submenu-text-faint)] hover:bg-[color:var(--sidebar-item-hover-bg)]'
            : 'text-[color:var(--sidebar-submenu-text-data)] hover:bg-[color:var(--sidebar-item-hover-bg)]'
      }`}
    >
      {sortableId && (
        <LeadingDragHandle
          left={contentLeft}
          size={TABLE_ICON_SIZE}
          listeners={listeners}
          attributes={attributes}
        />
      )}
      <Link
        to={Path.tableDefaultViewRecords(table.id)}
        // The full text of the truncated name, on the link (whose overlay covers
        // the row) so the row's own controls show only their tooltips.
        title={table.display_name_plural}
        className="flex min-w-0 flex-1 items-center self-stretch text-left after:absolute after:inset-0 after:rounded-md after:content-['']"
        aria-current={isActive ? 'page' : undefined}
        onClick={handleRouteClick}
      >
        <DatabaseIcon
          size={TABLE_ICON_SIZE}
          style={{ marginRight: TABLE_ICON_LABEL_GAP }}
          className={`shrink-0 ${sortableId ? DRAG_SWAPPED_ICON_CLASS : ''} ${isActive ? 'text-[color:var(--sidebar-item-active-text)]' : isHidden ? 'text-[color:var(--sidebar-submenu-text-faintest)]' : 'text-[color:var(--sidebar-submenu-text-faint)]'}`}
        />
        <FadeTruncatedText>{table.display_name_plural}</FadeTruncatedText>
        <MenuDescription description={table.description} className="ml-2" />
      </Link>
      {/* The row's options menu, its eye, then its heart in the last slot (menuIconSlots). */}
      <span
        onClick={(event) => event.stopPropagation()}
        className={`pointer-events-none relative z-10 ml-auto h-6 ${menuIconSlotsLeadClass(
          favoriteSlot !== 'collapsed'
        )} ${MENU_ICON_SLOTS_CLASS}`}
      >
        <span className={menuIconSlotClass(menuSlot)}>
          <DropdownMenu
            open={menuOpen}
            onOpenChange={setMenuOpen}
            sections={tableOptionsSections}
            align="right"
            placement="right-start"
            anchorElementRef={rowRef}
            className="min-w-52"
            trigger={
              <IconButton
                label="Table options"
                className={`pointer-events-auto ${MENU_ICON_SLOT_CLASS} text-[color:var(--sidebar-submenu-text-faintest)] transition-colors hover:text-[color:var(--sidebar-submenu-text-data)]`}
              >
                <KebabIcon size={14} aria-hidden="true" />
              </IconButton>
            }
          />
        </span>
        {/* A hidden table's eye and a favorite's heart always show; outlined ones
            only while hovered. */}
        <span className={`pointer-events-auto ${menuIconSlotClass(hiddenSlot)}`}>
          <HiddenToggle path={tablePath} name={table.display_name_plural} />
        </span>
        <span className={`pointer-events-auto ${menuIconSlotClass(favoriteSlot)}`}>
          <FavoriteToggle path={tablePath} variant="row" />
        </span>
      </span>
    </div>
  )
}

export default memo(TableLink)
